import type { Membership, Role } from './types';

/** Returns members whose assigned roles are allowed beneath every selected supervisor. */
export function eligibleSubordinateIds(
  memberships: Membership[],
  roleIdsByMembership: ReadonlyMap<string, readonly string[]>,
  roles: Role[],
  supervisorIds: readonly string[],
): Set<string> {
  if (supervisorIds.length === 0) return new Set();

  const roleById = new Map(roles.map((role) => [role.id, role]));
  const allRoleIds = new Set(roles.map((role) => role.id));
  const supervisorRules = supervisorIds.map((id) => {
    const supervisor = memberships.find((membership) => membership.id === id);
    if (!supervisor) return new Set<string>();
    if (supervisor.isCompanyOwner) return allRoleIds;

    const allowed = new Set<string>();
    for (const roleId of roleIdsByMembership.get(id) ?? []) {
      const role = roleById.get(roleId);
      for (const subordinateRoleId of role?.supervisesRoleIds ?? []) {
        if (roleById.has(subordinateRoleId)) allowed.add(subordinateRoleId);
      }
    }
    return allowed;
  });

  if (supervisorRules.some((allowed) => allowed.size === 0)) return new Set();

  return new Set(
    memberships
      .filter((membership) => !supervisorIds.includes(membership.id) && !membership.isCompanyOwner)
      .filter((membership) => {
        const assignedRoleIds = roleIdsByMembership.get(membership.id) ?? [];
        return assignedRoleIds.some((roleId) => supervisorRules.every((allowed) => allowed.has(roleId)));
      })
      .map((membership) => membership.id),
  );
}


/** Detects cycles in the configured role-to-role supervision graph. */
export function roleHierarchyHasCycle(
  roles: Role[],
  overrides: ReadonlyMap<string, readonly string[]> = new Map(),
): boolean {
  const roleIds = new Set(roles.map((role) => role.id));
  const visiting = new Set<string>();
  const visited = new Set<string>();

  function visit(roleId: string): boolean {
    if (visiting.has(roleId)) return true;
    if (visited.has(roleId)) return false;
    visiting.add(roleId);
    const role = roles.find((entry) => entry.id === roleId);
    const children = overrides.get(roleId) ?? role?.supervisesRoleIds ?? [];
    for (const childRoleId of children) {
      if (roleIds.has(childRoleId) && visit(childRoleId)) return true;
    }
    visiting.delete(roleId);
    visited.add(roleId);
    return false;
  }

  return roles.some((role) => visit(role.id));
}
